import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Plugs, Bluetooth, WifiHigh, ArrowsClockwise,
    X, Warning, PaperPlaneTilt
} from '@phosphor-icons/react';
import ByteLog from './ByteLog';

const CONN_ICONS = { uart: Plugs, bluetooth: Bluetooth, ble: Bluetooth, wifi: WifiHigh };

export default function ConnectionPanel({
    brands, selectedBrand, setSelectedBrand, selectedModel, setSelectedModel,
    connectionMode, connectionInfo, connecting, isHardwareConnected,
    readingParams, readAllParams, handleConnect, handleDisconnect,
    serialSupported, btSupported, rawHex, setRawHex, sendRawBytes,
    byteLog, faultCodes,
}) {
    return (
        <div className="space-y-4">
            <Card className="glass-card border-cyber-purple/20">
                <CardContent className="p-5">
                    <h3 className="font-display text-sm text-cyber-purple mb-3">SELECT CONTROLLER</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        {brands.map(b => (
                            <button key={b.id} onClick={() => { setSelectedBrand(b); setSelectedModel(b.models[0]); }}
                                data-testid={`brand-${b.id}`}
                                className={`p-3 rounded-xl border text-center transition-all text-xs font-display ${selectedBrand?.id === b.id ? 'border-cyber-purple bg-cyber-purple/10 text-cyber-purple' : 'border-white/10 bg-white/5 text-muted-foreground hover:border-white/20'}`}>
                                <p className="font-bold">{b.brand}</p>
                                <div className="flex justify-center gap-1 mt-1">
                                    {b.connection.map(c => { const I = CONN_ICONS[c] || Plugs; return <I key={c} size={12} />; })}
                                </div>
                            </button>
                        ))}
                    </div>
                    {selectedBrand && (
                        <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)}
                            data-testid="model-select"
                            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm mb-4">
                            {selectedBrand.models.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    )}

                    {connectionMode ? (
                        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20" data-testid="connection-status">
                            <div className="flex items-center gap-2 mb-2">
                                <div className={`w-3 h-3 rounded-full animate-pulse ${isHardwareConnected ? 'bg-primary' : 'bg-boltz-gold'}`} />
                                <span className={`text-sm font-bold ${isHardwareConnected ? 'text-primary' : 'text-boltz-gold'}`}>
                                    {isHardwareConnected ? 'Hardware Connected' : 'Simulated Mode'}
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-1">
                                {connectionInfo?.brand} {connectionInfo?.model} via {connectionInfo?.method?.toUpperCase()}
                            </p>
                            <p className="text-[10px] font-mono text-muted-foreground mb-3">
                                Baud: {connectionInfo?.serial_config?.baudRate} | {connectionInfo?.serial_config?.dataBits}N{connectionInfo?.serial_config?.stopBits}
                                {connectionMode === 'serial' && ' | Web Serial API'}
                                {connectionMode === 'bluetooth' && ' | Web Bluetooth API'}
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                <Button size="sm" onClick={readAllParams} disabled={readingParams}
                                    className="bg-primary text-black text-xs font-display" data-testid="read-params-btn">
                                    <ArrowsClockwise size={14} className={`mr-1 ${readingParams ? 'animate-spin' : ''}`} />
                                    {readingParams ? 'Reading...' : 'Read All Params'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={handleDisconnect}
                                    className="text-xs border-destructive/30 text-destructive" data-testid="disconnect-btn">
                                    <X size={14} className="mr-1" />Disconnect
                                </Button>
                            </div>
                            {isHardwareConnected && (
                                <div className="flex gap-2 mt-3">
                                    <Input value={rawHex} onChange={e => setRawHex(e.target.value)}
                                        placeholder="Send hex: 3A 00 01 00 3B"
                                        className="flex-1 font-mono text-xs" data-testid="raw-hex-input"
                                        onKeyDown={e => e.key === 'Enter' && sendRawBytes()} />
                                    <Button size="sm" onClick={sendRawBytes} variant="outline" className="text-xs">
                                        <PaperPlaneTilt size={14} />
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : selectedBrand ? (
                        <div>
                            <p className="text-xs text-muted-foreground mb-3">Connect via:</p>
                            <div className="flex gap-2">
                                {selectedBrand.connection.map(method => {
                                    const I = CONN_ICONS[method] || Plugs;
                                    const isReal = (method === 'uart' && serialSupported) || ((method === 'bluetooth' || method === 'ble') && btSupported);
                                    return (
                                        <Button key={method} onClick={() => handleConnect(method)} disabled={connecting}
                                            className="flex-1 bg-cyber-purple/10 text-cyber-purple border border-cyber-purple/20 text-xs font-display hover:bg-cyber-purple/20"
                                            data-testid={`connect-${method}`}>
                                            <I size={16} className="mr-1" />{method.toUpperCase()}
                                            {isReal && <span className="ml-1 text-[8px] text-primary">(HW)</span>}
                                        </Button>
                                    );
                                })}
                            </div>
                            {(!serialSupported && !btSupported) && (
                                <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-boltz-gold/5 border border-boltz-gold/20">
                                    <Warning size={14} className="text-boltz-gold shrink-0 mt-0.5" />
                                    <p className="text-[10px] text-boltz-gold/80">
                                        Your browser does not support Web Serial or Web Bluetooth. Connections will run in <strong>simulated mode</strong>. Use Chrome or Edge for real hardware access.
                                    </p>
                                </div>
                            )}
                            {(serialSupported || btSupported) && (
                                <p className="text-[10px] text-muted-foreground mt-3 text-center">
                                    {serialSupported && 'Web Serial API ready for UART. '}
                                    {btSupported && 'Web Bluetooth ready for BLE. '}
                                    Plug in your controller and click to connect.
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground text-center" data-testid="no-brand-selected">Select a controller brand above to begin</p>
                    )}

                    <ByteLog log={byteLog} />
                </CardContent>
            </Card>

            {faultCodes && (
                <Card className="glass-card">
                    <CardContent className="p-4">
                        <h3 className="font-display text-sm text-boltz-gold mb-3">FAULT CODE REFERENCE</h3>
                        <div className="space-y-1.5">
                            {Object.entries(faultCodes.fault_codes || {}).map(([code, info]) => (
                                <div key={code} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-white/5">
                                    <Badge className={`text-[10px] ${info.severity === 'critical' ? 'bg-plasma-red/10 text-plasma-red border-plasma-red/20' : 'bg-boltz-gold/10 text-boltz-gold border-boltz-gold/20'}`}>
                                        {code}
                                    </Badge>
                                    <span className="font-bold flex-shrink-0">{info.label}</span>
                                    <span className="text-muted-foreground truncate">{info.description}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
